
import React, { useContext, useState } from 'react';
import { AppContext } from '../App';
import { Transaction, Expense, Supplier, FinancialCategory } from '../types';
import { supabase } from '../supabase';

type FinTab = 'dash' | 'receivable' | 'payable' | 'suppliers' | 'categories' | 'reports';

const Financials: React.FC = () => {
  const context = useContext(AppContext);
  const [activeTab, setActiveTab] = useState<FinTab>('dash');

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isQuickAddClientOpen, setIsQuickAddClientOpen] = useState(false);
  const [isQuickAddSupplierOpen, setIsQuickAddSupplierOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [editingCategory, setEditingCategory] = useState<FinancialCategory | null>(null);
  const [categoryType, setCategoryType] = useState<'revenue' | 'expense'>('revenue');

  if (!context) return null;
  const {
    transactions, 
    expenses, 
    suppliers, clients, financialCategories, 
    syncAllData
  } = context;

  const totalReceivable = transactions.reduce((acc, t) => acc + t.amount, 0);
  const totalPaid = transactions.filter(t => t.status === 'Paid').reduce((acc, t) => acc + t.amount, 0);
  const totalPayable = expenses.reduce((acc, e) => acc + e.amount, 0);
  const netProfit = totalPaid - expenses.filter(e => e.status === 'Paid').reduce((acc, e) => acc + e.amount, 0);

  const handleSaveTransaction = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const clientName = formData.get('client') as string;
    const amount = parseFloat(formData.get('amount') as string);
    const date = formData.get('date') as string;
    const status = formData.get('status') as 'Paid' | 'Pending';
    const isInstallment = formData.get('isInstallment') === 'on';
    const installmentsCount = parseInt(formData.get('installmentsCount') as string) || 1;

    const baseTransaction = {
      client: clientName,
      plan: formData.get('plan') as string || 'Personalizado',
      status: status,
      initials: clientName.substring(0, 2).toUpperCase()
    };

    try {
      if (editingTransaction) {
        const { error } = await supabase
          .from('transactions')
          .update({ ...baseTransaction, amount, date })
          .eq('id', editingTransaction.id);
        
        if (error) throw error;
      } else {
        if (isInstallment && installmentsCount > 1) {
          const groupId = `GRP-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
          const newTransactions: any[] = [];
          const installmentAmount = amount / installmentsCount;

          for (let i = 1; i <= installmentsCount; i++) {
            const installmentDate = new Date(date); // Parsing pt-BR date might be tricky if it's strictly string. Assuming ISO or convertible.
            // If the input is text "10 Jan, 2026", new Date() might work or not depending on browser locale.
            // Ideally we should parse it correctly. But keeping original logic for now which seemed to rely on simple date string.
            // Wait, the input is type="text" with placeholder "Ex: 10 Jan, 2026". 
            // The original logic: const installmentDate = new Date(date);
            // We should be careful. 
            
            // Let's assume the user enters something parseable.
            
            if (!isNaN(installmentDate.getTime())) {
              installmentDate.setMonth(installmentDate.getMonth() + (i - 1));
            }

            newTransactions.push({
              id: `INV-${Math.floor(Math.random() * 900000) + 1000}-${i}`,
              ...baseTransaction,
              amount: installmentAmount,
              date: !isNaN(installmentDate.getTime()) ? installmentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : date,
              installment_group_id: groupId,
              installment_number: i,
              total_installments: installmentsCount
            });
          }
          
          const { error } = await supabase.from('transactions').insert(newTransactions);
          if (error) throw error;
        } else {
          const newTransaction = {
            id: `INV-${Math.floor(Math.random() * 900000) + 1000}`,
            ...baseTransaction,
            amount,
            date
          };
          const { error } = await supabase.from('transactions').insert(newTransaction);
          if (error) throw error;
        }
      }
      await syncAllData();
      closeModals();
    } catch (err) {
      console.error('Erro ao salvar transação:', err);
      alert('Erro ao salvar transação. Verifique o console.');
    }
  };

  const deleteTransaction = async (id: string) => {
    if (confirm('Excluir esta fatura permanentemente?')) {
      try {
        const { error } = await supabase.from('transactions').delete().eq('id', id);
        if (error) throw error;
        await syncAllData();
      } catch (err) {
        console.error('Erro ao excluir:', err);
        alert('Erro ao excluir.');
      }
    }
  };

  const handleSaveExpense = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const amount = parseFloat(formData.get('amount') as string);
    const dueDate = formData.get('dueDate') as string;
    const isInstallment = formData.get('isInstallment') === 'on';
    const installmentsCount = parseInt(formData.get('installmentsCount') as string) || 1;

    const baseExpense = {
      description: formData.get('description') as string,
      category: formData.get('category') as string,
      status: formData.get('status') as 'Paid' | 'Pending',
      supplier_id: formData.get('supplierId') as string
    };

    try {
      if (editingExpense) {
        const { error } = await supabase
          .from('expenses')
          .update({ ...baseExpense, amount, due_date: dueDate })
          .eq('id', editingExpense.id);
          
        if (error) throw error;
      } else {
        if (isInstallment && installmentsCount > 1) {
          const groupId = `EXPGRP-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
          const newExpenses: any[] = [];
          const installmentAmount = amount / installmentsCount;

          for (let i = 1; i <= installmentsCount; i++) {
            const installmentDate = new Date(dueDate);
            if (!isNaN(installmentDate.getTime())) {
              installmentDate.setMonth(installmentDate.getMonth() + (i - 1));
            }

            newExpenses.push({
              id: `EXP-${Math.floor(Math.random() * 900000) + 1000}-${i}`,
              ...baseExpense,
              amount: installmentAmount,
              due_date: !isNaN(installmentDate.getTime()) ? installmentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : dueDate,
              installment_group_id: groupId,
              installment_number: i,
              total_installments: installmentsCount
            });
          }
          const { error } = await supabase.from('expenses').insert(newExpenses);
          if (error) throw error;
        } else {
          const newExpense = {
            id: `EXP-${Math.floor(Math.random() * 900000) + 1000}`,
            ...baseExpense,
            amount,
            due_date: dueDate
          };
          const { error } = await supabase.from('expenses').insert(newExpense);
          if (error) throw error;
        }
      }
      await syncAllData();
      closeModals();
    } catch (err) {
      console.error('Erro ao salvar despesa:', err);
      alert('Erro ao salvar despesa.');
    }
  };

  const deleteExpense = async (id: string) => {
    if (confirm('Excluir esta despesa permanentemente?')) {
      try {
        const { error } = await supabase.from('expenses').delete().eq('id', id);
        if (error) throw error;
        await syncAllData();
      } catch (err) {
        console.error('Erro ao excluir:', err);
        alert('Erro ao excluir.');
      }
    }
  };

  const closeModals = () => {
    setIsTransactionModalOpen(false);
    setIsExpenseModalOpen(false);
    setIsCategoryModalOpen(false);
    setIsQuickAddClientOpen(false);
    setIsQuickAddSupplierOpen(false);
    setEditingTransaction(null);
    setEditingExpense(null);
    setEditingCategory(null);
    setCategoryType('revenue');
  };

  const handleSaveCategory = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const type = categoryType;

    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('financial_categories')
          .update({ name, type })
          .eq('id', editingCategory.id);
        if (error) throw error;
      } else {
        const newCategory = {
          id: `CAT-${Math.floor(Math.random() * 9000) + 1000}`,
          name,
          type
        };
        const { error } = await supabase.from('financial_categories').insert(newCategory);
        if (error) throw error;
      }
      await syncAllData();
      closeModals();
    } catch (err) {
      console.error('Erro ao salvar categoria:', err);
      alert('Erro ao salvar categoria.');
    }
  };

  const deleteCategory = async (id: string) => {
    if (confirm('Excluir esta categoria? Isso pode afetar lançamentos existentes.')) {
      try {
        const { error } = await supabase.from('financial_categories').delete().eq('id', id);
        if (error) throw error;
        await syncAllData();
      } catch (err) {
        console.error('Erro ao excluir categoria:', err);
        alert('Erro ao excluir categoria.');
      }
    }
  };

  const openNewOperation = () => {
    if (activeTab === 'payable') {
      setIsExpenseModalOpen(true);
    } else {
      setIsTransactionModalOpen(true);
    }
  };

  const renderDashboard = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="lg:col-span-2 flex flex-col gap-6 md:gap-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
            <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Saldo em Caixa</p>
            <h3 className="text-3xl md:text-4xl font-black text-primary mt-3 tracking-tighter">R$ {netProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
            <div className="mt-6 flex items-center gap-2 text-[10px] md:text-[11px] font-bold text-green-500 bg-green-50 w-fit px-3 py-1 rounded-full">
              <span className="material-symbols-outlined text-[14px] md:text-[16px]">trending_up</span> +12% vs mês anterior
            </div>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
            <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Previsão Mês (Bruto)</p>
            <h3 className="text-3xl md:text-4xl font-black text-text-main mt-3 tracking-tighter">R$ {totalReceivable.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
            <p className="text-[10px] md:text-[11px] font-bold text-text-secondary mt-4 uppercase tracking-wider">Aguardando faturas</p>
          </div>
        </div>

        <div className="bg-white p-6 md:p-10 rounded-[23px] border border-gray-100 shadow-sm flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 md:mb-10">
            <h4 className="text-lg md:text-xl font-black text-text-main tracking-tight">Fluxo Semanal</h4>
            <select className="bg-gray-50 border-none rounded-xl text-[10px] font-black uppercase tracking-widest px-4 h-10 w-fit">
              <option>Últimos 7 dias</option>
              <option>Últimos 30 dias</option>
            </select>
          </div>
          <div className="flex items-end justify-between h-40 md:h-48 gap-2 md:gap-4 px-1 md:px-2">
            {[65, 45, 80, 55, 90, 40, 70].map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2 md:gap-3">
                <div className="w-full bg-primary-light rounded-xl md:rounded-2xl relative group cursor-pointer" style={{ height: `${h}%` }}>
                  <div className="absolute inset-x-0 bottom-0 bg-primary rounded-xl md:rounded-2xl h-1/2 group-hover:h-2/3 transition-all" />
                </div>
                <span className="text-[8px] md:text-[9px] font-black text-gray-300 uppercase tracking-widest">
                  {['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'][i]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="bg-text-main p-6 md:p-8 rounded-[23px] text-white">
          <h4 className="text-base md:text-lg font-black tracking-tight mb-6">Contas Próximas</h4>
          <div className="space-y-4">
            {expenses.slice(0, 3).map(e => (
              <div key={e.id} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                <div className="min-w-0 pr-2">
                  <p className="text-sm font-black truncate">{e.description}</p>
                  <p className="text-[8px] md:text-[9px] font-bold text-white/40 uppercase tracking-widest mt-1">{e.dueDate}</p>
                </div>
                <p className="text-sm font-black text-primary-light shrink-0">R$ {e.amount}</p>
              </div>
            ))}
          </div>
          <button onClick={() => setActiveTab('payable')} className="w-full mt-6 py-4 rounded-2xl bg-white/10 text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all">Ver Detalhes</button>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex-1">
          <h4 className="text-base md:text-lg font-black text-text-main tracking-tight mb-6">Receita por Cliente</h4>
          <div className="space-y-5">
            {clients.slice(0, 4).map(c => (
              <div key={c.id} className="flex items-center gap-4">
                <img src={c.logoUrl} className="size-8 md:size-10 rounded-lg md:rounded-xl object-cover bg-gray-50 shrink-0" alt="" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-sm font-black text-text-main truncate">{c.name}</p>
                  <div className="w-full bg-gray-50 h-1 rounded-full mt-2 overflow-hidden">
                    <div className="bg-primary h-full rounded-full" style={{ width: '70%' }}></div>
                  </div>
                </div>
                <p className="text-[10px] md:text-xs font-black text-text-main shrink-0">R$ 15k</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFinancialList = (data: any[], type: 'rev' | 'exp') => (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="hidden md:block bg-white rounded-[23px] border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100">
              <th className="px-10 py-7 text-[10px] font-black text-text-secondary uppercase tracking-widest">{type === 'rev' ? 'Cliente / Fatura' : 'Descrição / Categoria'}</th>
              <th className="px-10 py-7 text-[10px] font-black text-text-secondary uppercase tracking-widest">Valor</th>
              <th className="px-10 py-7 text-[10px] font-black text-text-secondary uppercase tracking-widest">Data</th>
              <th className="px-10 py-7 text-[10px] font-black text-text-secondary uppercase tracking-widest">Status</th>
              <th className="px-10 py-7 text-right text-[10px] font-black text-text-secondary uppercase tracking-widest">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.map((item) => (
              <tr key={item.id} className="hover:bg-primary/[0.01] transition-all group">
                <td className="px-10 py-7">
                  <div className="flex items-center gap-4">
                    <div className="size-10 rounded-xl bg-gray-50 flex items-center justify-center text-[10px] font-black text-primary border border-gray-100 uppercase">
                      {type === 'rev' ? item.initials : item.category.substring(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-black text-text-main truncate group-hover:text-primary transition-colors">{type === 'rev' ? item.client : item.description}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">{item.id}</p>
                        {item.installmentNumber && (
                          <span className="text-[8px] font-black bg-primary/10 text-primary px-1.5 py-0.5 rounded uppercase">
                            Parcela {item.installmentNumber}/{item.totalInstallments}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-10 py-7">
                  <p className="text-base font-black text-text-main tracking-tight">R$ {item.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </td>
                <td className="px-10 py-7 text-xs font-bold text-text-secondary uppercase tracking-widest">{type === 'rev' ? item.date : item.dueDate}</td>
                <td className="px-10 py-7">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${item.status === 'Paid' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                    {item.status === 'Paid' ? 'Liquidado' : 'Pendente'}
                  </span>
                </td>
                <td className="px-10 py-7 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => type === 'rev' ? (setEditingTransaction(item), setIsTransactionModalOpen(true)) : (setEditingExpense(item), setIsExpenseModalOpen(true))} className="size-9 flex items-center justify-center rounded-xl bg-gray-50 text-text-main hover:text-primary transition-all">
                      <span className="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    <button onClick={() => type === 'rev' ? deleteTransaction(item.id) : deleteExpense(item.id)} className="size-9 flex items-center justify-center rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-all">
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden grid grid-cols-1 gap-4">
        {data.map((item) => (
          <div key={item.id} className="bg-white p-6 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`size-12 rounded-2xl flex items-center justify-center text-sm font-black border border-gray-100 ${item.status === 'Paid' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                  {type === 'rev' ? item.initials : item.category.substring(0, 2)}
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-black text-text-main truncate max-w-[150px]">{type === 'rev' ? item.client : item.description}</h4>
                  <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-0.5">{item.id}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => type === 'rev' ? (setEditingTransaction(item), setIsTransactionModalOpen(true)) : (setEditingExpense(item), setIsExpenseModalOpen(true))} className="text-gray-400 hover:text-primary transition-colors">
                  <span className="material-symbols-outlined text-[20px]">edit</span>
                </button>
                <button onClick={() => type === 'rev' ? deleteTransaction(item.id) : deleteExpense(item.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                  <span className="material-symbols-outlined text-[20px]">delete</span>
                </button>
              </div>
            </div>

            <div className="flex justify-between items-end mt-2 pt-4 border-t border-gray-50">
              <div>
                <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mb-1">Valor Total</p>
                <p className="text-xl font-black text-text-main tracking-tight">R$ {item.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="text-right">
                <p className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full mb-2 inline-block ${item.status === 'Paid' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                  {item.status === 'Paid' ? 'Liquidado' : 'Pendente'}
                </p>
                <p className="text-xs font-bold text-text-secondary uppercase">{type === 'rev' ? item.date : item.dueDate}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSuppliers = () => (
    <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {suppliers.map(supplier => (
          <div key={supplier.id} className="bg-white p-8 rounded-[23px] shadow-sm border border-gray-100 flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="size-12 rounded-2xl bg-primary/5 text-primary flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl">business</span>
              </div>
              <span className="px-3 py-1 rounded-full bg-gray-100 text-[9px] font-black uppercase tracking-widest text-text-secondary">{supplier.category}</span>
            </div>
            <div>
              <h4 className="text-lg font-black text-text-main leading-tight">{supplier.name}</h4>
              <p className="text-xs text-text-secondary font-bold mt-1">{supplier.contactName}</p>
            </div>
            <div className="pt-4 border-t border-gray-50 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-[10px] font-bold text-text-secondary">
                <span className="material-symbols-outlined text-sm">mail</span>
                {supplier.email}
              </div>
              <div className="flex items-center gap-2 text-[10px] font-bold text-text-secondary">
                <span className="material-symbols-outlined text-sm">call</span>
                {supplier.phone}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCategories = () => (
    <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex justify-between items-center bg-white p-8 rounded-[23px] shadow-sm border border-gray-100">
        <div>
          <h3 className="text-xl font-black text-text-main">Plano de Contas</h3>
          <p className="text-xs text-text-secondary font-bold mt-1 uppercase tracking-widest">Gerencie categorias de receitas e despesas</p>
        </div>
        <button onClick={() => { setEditingCategory(null); setCategoryType('revenue'); setIsCategoryModalOpen(true); }} className="bg-primary text-white h-12 px-6 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-105 transition-all">
          Nova Categoria
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Receitas */}
        <div className="bg-white p-8 rounded-[23px] shadow-sm border border-gray-100">
          <h4 className="text-sm font-black text-primary uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">trending_up</span>
            Categorias de Receita
          </h4>
          <div className="flex flex-col gap-3">
            {financialCategories.filter(c => c.type === 'revenue').map(category => (
              <div key={category.id} className="group flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                <span className="text-sm font-bold text-text-main">{category.name}</span>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditingCategory(category); setCategoryType(category.type); setIsCategoryModalOpen(true); }} className="size-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-base">edit</span>
                  </button>
                  <button onClick={() => deleteCategory(category.id)} className="size-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Despesas */}
        <div className="bg-white p-8 rounded-[23px] shadow-sm border border-gray-100">
          <h4 className="text-sm font-black text-red-500 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">trending_down</span>
            Categorias de Despesa
          </h4>
          <div className="flex flex-col gap-3">
            {financialCategories.filter(c => c.type === 'expense').map(category => (
              <div key={category.id} className="group flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                <span className="text-sm font-bold text-text-main">{category.name}</span>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditingCategory(category); setCategoryType(category.type); setIsCategoryModalOpen(true); }} className="size-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-base">edit</span>
                  </button>
                  <button onClick={() => deleteCategory(category.id)} className="size-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-7xl mx-auto flex flex-col gap-6 md:gap-10">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Financeiro</h2>
          <p className="text-text-secondary font-bold text-base md:text-lg">Gestão operacional de caixa.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full md:w-auto">
          <button onClick={() => setActiveTab('reports')} className="h-14 px-8 rounded-2xl border-2 border-primary/20 text-primary font-black uppercase text-[10px] tracking-widest hover:bg-primary-light transition-all flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-[18px]">analytics</span> Relatórios DRE
          </button>
          <button onClick={openNewOperation} className="h-14 px-8 rounded-2xl bg-primary text-white font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-[18px]">add</span> Nova Operação
          </button>
        </div>
      </header>

      <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide pb-2 md:pb-0">
        <div className="flex gap-2 p-1.5 bg-gray-100/60 rounded-[23px] w-fit min-w-full md:min-w-0">
          {[
            { id: 'dash', label: 'Painel' },
            { id: 'receivable', label: 'Receitas' },
            { id: 'payable', label: 'Despesas' },
            { id: 'suppliers', label: 'Parceiros' },
            { id: 'categories', label: 'Plano de Contas' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as FinTab)} className={`px-6 md:px-8 py-3.5 rounded-[22px] text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-1 md:flex-none ${activeTab === tab.id ? 'bg-white shadow-md text-primary' : 'text-text-secondary hover:text-text-main'}`}>{tab.label}</button>
          ))}
        </div>
      </div>

      <div className="flex-1">
        {activeTab === 'dash' && renderDashboard()}
        {activeTab === 'receivable' && renderFinancialList(transactions, 'rev')}
        {activeTab === 'payable' && renderFinancialList(expenses, 'exp')}
        {activeTab === 'suppliers' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 animate-in slide-in-from-right-4 duration-500">
            {suppliers.map(s => (
              <div key={s.id} className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm group hover:border-primary/20 transition-all">
                <div className="flex justify-between items-start mb-6">
                  <div className="size-14 rounded-2xl bg-primary-light text-primary flex items-center justify-center font-black uppercase text-lg border border-primary/10">{s.name.substring(0, 1)}</div>
                  <span className="text-[9px] font-black uppercase bg-gray-50 text-text-secondary px-2.5 py-1 rounded-lg tracking-widest">{s.category}</span>
                </div>
                <h4 className="text-lg md:text-xl font-black text-text-main group-hover:text-primary transition-colors truncate">{s.name}</h4>
                <p className="text-[10px] font-bold text-text-secondary mt-1">{s.cnpj}</p>
                <div className="mt-8 pt-6 border-t border-gray-50 flex items-center justify-between">
                  <div className="flex flex-col gap-1 min-w-0">
                    <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest">Contato</p>
                    <p className="text-xs font-bold text-text-main truncate">{s.contactName}</p>
                  </div>
                  <button className="size-10 rounded-full bg-gray-50 text-text-secondary hover:bg-primary-light hover:text-primary transition-all flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-[18px]">call</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {activeTab === 'categories' && renderCategories()}
        {activeTab === 'reports' && (
          <div className="bg-white p-6 md:p-12 rounded-[23px] border border-gray-100 shadow-sm animate-in zoom-in-95 duration-500">
            <h3 className="text-xl md:text-2xl font-black text-text-main tracking-tight mb-8 md:mb-10 text-center md:text-left">DRE Mensal</h3>
            <div className="space-y-4 md:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-6 bg-green-50/50 rounded-[23px] border border-green-100 gap-2">
                <span className="text-[11px] md:text-sm font-black text-green-700 uppercase tracking-widest text-center sm:text-left">(+) Receita Bruta</span>
                <span className="text-base md:text-lg font-black text-green-700 text-center sm:text-right">R$ {totalReceivable.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-6 bg-red-50/50 rounded-[23px] border border-red-100 gap-2">
                <span className="text-[11px] md:text-sm font-black text-red-700 uppercase tracking-widest text-center sm:text-left">(-) Custo Operacional</span>
                <span className="text-base md:text-lg font-black text-red-700 text-center sm:text-right">R$ {totalPayable.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-8 bg-text-main rounded-[23px] text-white shadow-xl gap-4">
                <span className="text-base md:text-lg font-black uppercase tracking-[0.2em] text-primary-light text-center sm:text-left">(=) Lucro Líquido</span>
                <span className="text-2xl md:text-3xl font-black text-white text-center sm:text-right">R$ {(totalReceivable - totalPayable).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {isTransactionModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-text-main/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-xl rounded-[23px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between shrink-0 bg-white">
              <h3 className="text-2xl font-black text-text-main tracking-tight">
                {editingTransaction ? 'Editar Fatura' : 'Nova Fatura'}
              </h3>
              <button onClick={closeModals} className="size-12 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all text-text-secondary">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSaveTransaction} className="p-8 flex flex-col gap-6 overflow-y-auto">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Cliente</label>
                <div className="flex gap-2">
                  <select name="client" defaultValue={editingTransaction?.client} required className="flex-1 h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all">
                    <option value="">Selecione um cliente...</option>
                    {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  <button type="button" onClick={() => setIsQuickAddClientOpen(true)} className="size-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-all">
                    <span className="material-symbols-outlined">person_add</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Plano / Categoria</label>
                  <select name="plan" defaultValue={editingTransaction?.plan} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all">
                    <option value="Personalizado">Personalizado</option>
                    {financialCategories.filter(c => c.type === 'revenue').map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Valor Total (R$)</label>
                  <input name="amount" type="number" step="0.01" defaultValue={editingTransaction?.amount} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Data de Emissão</label>
                  <input name="date" type="text" defaultValue={editingTransaction?.date || new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" placeholder="Ex: 10 Jan, 2026" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Parcelamento</label>
                  <div className="flex gap-2 items-center h-14 bg-gray-50 rounded-2xl border-2 border-gray-200 px-4">
                    <input type="checkbox" name="isInstallment" className="size-5 accent-primary" />
                    <span className="text-xs font-bold text-text-main mr-2">Parcelar em:</span>
                    <input name="installmentsCount" type="number" min="2" max="48" defaultValue="2" className="flex-1 bg-transparent outline-none font-black text-sm text-primary" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Status do Pagamento</label>
                <div className="flex gap-4">
                  {['Paid', 'Pending'].map(s => (
                    <label key={s} className={`flex-1 flex items-center justify-center h-14 rounded-2xl border-2 transition-all cursor-pointer font-black text-[10px] uppercase tracking-widest ${editingTransaction?.status === s || (!editingTransaction && s === 'Pending') ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 bg-gray-50 text-text-secondary hover:border-gray-300'}`}>
                      <input type="radio" name="status" value={s} defaultChecked={editingTransaction?.status === s || (!editingTransaction && s === 'Pending')} className="hidden" />
                      {s === 'Paid' ? 'Liquidado' : 'Pendente'}
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-2xl shadow-primary/40 hover:scale-[1.01] transition-all mt-2 shrink-0">
                {editingTransaction ? 'Salvar Alterações' : 'Gerar Fatura'}
              </button>
            </form>
          </div>
        </div>
      )}

      {isExpenseModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-text-main/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-xl rounded-[23px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between shrink-0 bg-white">
              <h3 className="text-2xl font-black text-text-main tracking-tight">
                {editingExpense ? 'Editar Despesa' : 'Nova Despesa'}
              </h3>
              <button onClick={closeModals} className="size-12 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all text-text-secondary">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSaveExpense} className="p-8 flex flex-col gap-6 overflow-y-auto">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Descrição do Custo</label>
                <input name="description" defaultValue={editingExpense?.description} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" placeholder="Ex: Assinatura Google Workspace" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Plano de Contas</label>
                  <select name="category" defaultValue={editingExpense?.category} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all">
                    <option value="Outros">Selecione...</option>
                    {financialCategories.filter(c => c.type === 'expense').map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                    <optgroup label="Padrão">
                      <option value="Infra">Infraestrutura</option>
                      <option value="TI">TI / Software</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Aluguel">Aluguel / Mobiliário</option>
                      <option value="Equipe">Pessoal / Equipe</option>
                    </optgroup>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Parceiro / Fornecedor</label>
                  <div className="flex gap-2">
                    <select name="supplierId" defaultValue={editingExpense?.supplierId} className="flex-1 h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all">
                      <option value="">Nenhum...</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button type="button" onClick={() => setIsQuickAddSupplierOpen(true)} className="size-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-all">
                      <span className="material-symbols-outlined">add_business</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Valor Total (R$)</label>
                  <input name="amount" type="number" step="0.01" defaultValue={editingExpense?.amount} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Vencimento</label>
                  <input name="dueDate" type="text" defaultValue={editingExpense?.dueDate} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" placeholder="Ex: 05 Fev, 2026" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Status do Lançamento</label>
                  <div className="flex gap-4">
                    {['Paid', 'Pending'].map(s => (
                      <label key={s} className={`flex-1 flex items-center justify-center h-14 rounded-2xl border-2 transition-all cursor-pointer font-black text-[10px] uppercase tracking-widest ${editingExpense?.status === s || (!editingExpense && s === 'Pending') ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 bg-gray-50 text-text-secondary hover:border-gray-300'}`}>
                        <input type="radio" name="status" value={s} defaultChecked={editingExpense?.status === s || (!editingExpense && s === 'Pending')} className="hidden" />
                        {s === 'Paid' ? 'Liquidado' : 'Pendente'}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Parcelamento</label>
                  <div className="flex gap-2 items-center h-14 bg-gray-50 rounded-2xl border-2 border-gray-200 px-4">
                    <input type="checkbox" name="isInstallment" className="size-5 accent-primary" />
                    <span className="text-xs font-bold text-text-main mr-2">Parcelar em:</span>
                    <input name="installmentsCount" type="number" min="2" max="48" defaultValue="2" className="flex-1 bg-transparent outline-none font-black text-sm text-primary" />
                  </div>
                </div>
              </div>

              <button type="submit" className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-2xl shadow-primary/40 hover:scale-[1.01] transition-all mt-2 shrink-0">
                {editingExpense ? 'Salvar Alterações' : 'Registrar Despesa'}
              </button>
            </form>
          </div>
        </div>
      )}

      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-text-main/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-lg rounded-[23px] shadow-2xl p-10 animate-in zoom-in-95 duration-300">
            <div className="flex justify-between items-center mb-10">
              <div>
                <h3 className="text-2xl font-black text-text-main tracking-tight">{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</h3>
                <p className="text-xs text-text-secondary font-bold mt-1 uppercase tracking-widest">Plano de Contas</p>
              </div>
              <button onClick={closeModals} className="size-12 rounded-2xl bg-gray-50 text-text-secondary hover:bg-red-50 hover:text-red-500 transition-all flex items-center justify-center">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSaveCategory} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Nome da Categoria</label>
                <input name="name" type="text" defaultValue={editingCategory?.name} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" placeholder="Ex: Marketing, Aluguel, Consultoria..." />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Tipo de Lançamento</label>
                <div className="flex gap-4">
                  {[
                    { id: 'revenue', label: 'Receita', icon: 'trending_up', color: 'primary' },
                    { id: 'expense', label: 'Despesa', icon: 'trending_down', color: 'red-500' }
                  ].map(t => (
                    <label key={t.id} onClick={() => setCategoryType(t.id as 'revenue' | 'expense')} className={`flex-1 flex flex-col items-center justify-center p-6 rounded-[23px] border-2 transition-all cursor-pointer gap-2 ${categoryType === t.id ? 'border-primary bg-primary/5 text-primary shadow-lg shadow-primary/5' : 'border-gray-100 bg-gray-50 text-text-secondary hover:border-gray-200'}`}>
                      <input type="radio" name="type" value={t.id} checked={categoryType === t.id} readOnly className="sr-only" />
                      <span className={`material-symbols-outlined text-2xl ${categoryType === t.id ? 'text-primary' : 'text-text-secondary'}`}>{t.icon}</span>
                      <span className="font-black text-[10px] uppercase tracking-widest">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-2xl shadow-primary/40 hover:scale-[1.01] transition-all mt-4">
                {editingCategory ? 'Salvar Alterações' : 'Criar Categoria'}
              </button>
            </form>
          </div>
        </div>
      )}

      {isQuickAddClientOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-text-main/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-md rounded-[23px] shadow-2xl p-8 animate-in zoom-in-95 duration-300">
            <h4 className="text-xl font-black text-text-main mb-6">Cadastro Rápido: Cliente</h4>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const name = new FormData(e.currentTarget).get('name') as string;
              try {
                const { error } = await supabase.from('clients').insert({
                  id: `CL-${Math.floor(Math.random() * 9000) + 1000}`,
                  name,
                  type: 'Avulso',
                  contact_name: name,
                  contact_role: 'Proprietário',
                  email: '',
                  phone: '',
                  cnpj: '',
                  address: '',
                  status: 'Active',
                  avatar_url: '',
                  logo_url: `https://ui-avatars.com/api/?name=${name}&background=random`,
                  active_projects_count: 0
                });
                if (error) throw error;
                await syncAllData();
                setIsQuickAddClientOpen(false);
              } catch (err) {
                console.error(err);
                alert('Erro ao adicionar cliente');
              }
            }} className="flex flex-col gap-4">
              <input name="name" placeholder="Nome do Cliente" required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-100 outline-none font-bold" autoFocus />
              <div className="flex gap-3">
                <button type="button" onClick={() => setIsQuickAddClientOpen(false)} className="flex-1 h-14 rounded-2xl bg-gray-100 font-black uppercase text-[10px] tracking-widest">Cancelar</button>
                <button type="submit" className="flex-1 h-14 rounded-2xl bg-primary text-white font-black uppercase text-[10px] tracking-widest shadow-lg shadow-primary/20">Cadastrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isQuickAddSupplierOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-text-main/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-md rounded-[23px] shadow-2xl p-8 animate-in zoom-in-95 duration-300">
            <h4 className="text-xl font-black text-text-main mb-6">Cadastro Rápido: Fornecedor</h4>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const name = new FormData(e.currentTarget).get('name') as string;
              try {
                const { error } = await supabase.from('suppliers').insert({
                  id: `SUP-${Math.floor(Math.random() * 9000) + 1000}`,
                  name,
                  category: 'Outros',
                  cnpj: '',
                  contact_name: name,
                  email: '',
                  phone: ''
                });
                if (error) throw error;
                await syncAllData();
                setIsQuickAddSupplierOpen(false);
              } catch (err) {
                console.error(err);
                alert('Erro ao adicionar fornecedor');
              }
            }} className="flex flex-col gap-4">
              <input name="name" placeholder="Nome do Fornecedor" required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-100 outline-none font-bold" autoFocus />
              <div className="flex gap-3">
                <button type="button" onClick={() => setIsQuickAddSupplierOpen(false)} className="flex-1 h-14 rounded-2xl bg-gray-100 font-black uppercase text-[10px] tracking-widest">Cancelar</button>
                <button type="submit" className="flex-1 h-14 rounded-2xl bg-primary text-white font-black uppercase text-[10px] tracking-widest shadow-lg shadow-primary/20">Cadastrar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Financials;
